import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Edit as EditIcon } from '@mui/icons-material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { MonetizationOn as MonetizationOnIcon } from '@mui/icons-material';
import { getBudgetTemplates } from '../../services/budgetService';
import { formatCurrency } from '../../../../utils/formatters';

/**
 * Component for displaying budget templates
 */
const BudgetTemplateList = () => {
  // State for templates data and UI
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load templates on component mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Fetch templates from API
  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getBudgetTemplates();
      setTemplates(response.data);
    } catch (err) {
      setError('Failed to load budget templates. Please try again.');
      console.error('Error fetching budget templates:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle creating a new template (to be implemented)
  const handleCreateTemplate = () => {
    // Implementation for creating a new template would go here
    console.log('Create template clicked');
  };

  // Handle editing a template (to be implemented)
  const handleEditTemplate = (template) => {
    // Implementation for editing a template would go here
    console.log('Edit template:', template);
  };

  // Handle duplicating a template (to be implemented)
  const handleDuplicateTemplate = (template) => {
    // Implementation for duplicating a template would go here
    console.log('Duplicate template:', template);
  };

  // Handle deleting a template (to be implemented)
  const handleDeleteTemplate = (template) => {
    // Implementation for deleting a template would go here
    console.log('Delete template:', template);
  };

  return (
    <Box>
      <Card>
        <CardHeader
          title="Budget Templates"
          action={
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateTemplate}
              disabled={loading}
            >
              Create Template
            </Button>
          }
        />
        
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {loading && templates.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : templates.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="textSecondary">
                No budget templates found. Create your first template to start automating your budgets.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Categories</TableCell>
                    <TableCell>Total Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template._id}>
                      <TableCell>
                        <Typography variant="body1">{template.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {template.description || 'No description'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {template.categories?.length || 0} categories
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <MonetizationOnIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {formatCurrency(template.totalAmount)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {template.isDefault ? (
                          <Chip label="Default" color="primary" size="small" />
                        ) : (
                          <Chip label="Regular" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditTemplate(template)}
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate">
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => handleDuplicateTemplate(template)}
                            disabled={loading}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => handleDeleteTemplate(template)}
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BudgetTemplateList; 